<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { HeadersData } from '@/composables/useTable';

import { computed } from 'vue';

import {
  percentFormat,
  utcDateStrToUserZonedDateStrFormat,
} from '@/utils/format';
import TableLoader from '@/components/loaders/TableLoader.vue';
import EmDashPlaceholder from '@/components/placeholders/EmDashPlaceholder.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import useTable from '@/composables/useTable';
import { useUserStore } from '@/stores/useUserStore';
import { WeeklyStats } from '@/models/reports';

import tableStyles from '@/styles/table.module.scss';

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<WeeklyStats[]>;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const userStore = useUserStore();

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<WeeklyStats[]>>
>;

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'date',
      title: 'Date',
      isNotSortable: true,
    },
    {
      value: 'total',
      title: 'Total',
      isNotSortable: true,
    },
    {
      value: 'completed',
      title: 'Completed',
      isNotSortable: true,
    },
    {
      value: 'uncompleted',
      title: 'Uncompleted',
      isNotSortable: true,
    },
    {
      value: 'percentage',
      title: 'Percentage',
      isNotSortable: true,
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length;
});

const userTimeZone = computed(() => userStore.user.timezone);

function dateStrItemExt(value: unknown): string {
  return utcDateStrToUserZonedDateStrFormat(
    value,
    userTimeZone.value,
    'yyyy-MM-dd',
  );
}
</script>

<template>
  <table
    :class="[tableStyles.table, $style.weeklyReportTable]"
    data-testid="weeklyReportTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.date">
        <tr>
          <td :class="$style.dateTd">
            <EmDashPlaceholder :item="dataItem.date" :ext="dateStrItemExt" />
          </td>
          <td :class="$style.totalTd">
            <EmDashPlaceholder :item="dataItem.total" />
          </td>
          <td :class="$style.completedTd">
            <EmDashPlaceholder :item="dataItem.completed" />
          </td>
          <td :class="$style.uncompletedTd">
            <EmDashPlaceholder :item="dataItem.uncompleted" />
          </td>
          <td :class="$style.percentageTd">
            <EmDashPlaceholder
              :item="dataItem.percentage"
              :ext="percentFormat"
            />
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
.weeklyReportTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
  }
}
  .dateTh {
  }
  .totalTh {
  }
  .completedTh {
  }
  .uncompletedTh {
  }
  .percentageTh {
  }
  .dateTd {
    white-space: nowrap;
  }
  .totalTd {
    white-space: nowrap;
  }
  .completedTd {
    white-space: nowrap;
  }
  .uncompletedTd {
    white-space: nowrap;
  }
  .percentageTd {
    white-space: nowrap;
  }
</style>
